import { useState, useEffect, useCallback } from 'react'
import { Book, Plus, Trash2, ChevronRight, ExternalLink, X, Loader2, Search, StickyNote, Building2, Upload, ChevronDown, FileSpreadsheet, Clock } from 'lucide-react'
import { vendorApiDocsApi, type VendorApiVendor, type VendorApiLink, type VendorSourceReport } from '@api/client'
import * as XLSX from 'xlsx'
import { COUNTRY_CODES } from '../countryCodes'
import { useAuthStore } from '@store/authStore'

function formatDate(dateStr: string | null | undefined) {
  if (!dateStr) return null
  const iso = dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

// Reverse lookup: code → name
const CODE_TO_NAME: Record<string, string> = {}
for (const [name, code] of Object.entries(COUNTRY_CODES)) {
  if (!CODE_TO_NAME[code]) CODE_TO_NAME[code] = name
}

// ─── Add Vendor Modal ─────────────────────────────────────────────────

function AddVendorModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [name, setName] = useState('')
  const [docsUrl, setDocsUrl] = useState('')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) return
    setSaving(true)
    const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')
    const res = await vendorApiDocsApi.createVendor({ name: name.trim(), slug, docs_base_url: docsUrl.trim() || undefined })
    if (res.success) { onAdded(); onClose() }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--bg-primary)] rounded-xl border border-[var(--border-color)] w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
          <h3 className="font-semibold text-[var(--text-primary)]">Add Vendor</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1">Vendor Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. DataZoo" autoFocus
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]" />
          </div>
          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1">Docs Base URL (optional)</label>
            <input value={docsUrl} onChange={e => setDocsUrl(e.target.value)} placeholder="e.g. https://docs.datazoo.com/api"
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]" />
          </div>
          <button onClick={handleSave} disabled={!name.trim() || saving}
            className="w-full py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-50 transition-colors">
            {saving ? 'Adding…' : 'Add Vendor'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Add Link Modal ───────────────────────────────────────────────────

function AddLinkModal({ vendor, onClose, onAdded }: { vendor: VendorApiVendor; onClose: () => void; onAdded: () => void }) {
  const [countrySearch, setCountrySearch] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<{ code: string; name: string } | null>(null)
  const [serviceName, setServiceName] = useState('')
  const [docUrl, setDocUrl] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const user = useAuthStore(s => s.user)

  const countryEntries = Object.entries(COUNTRY_CODES)
    .map(([name, code]) => ({ name, code }))
    .filter(e => !countrySearch || e.name.toLowerCase().includes(countrySearch.toLowerCase()) || e.code.toLowerCase().includes(countrySearch.toLowerCase()))
    .slice(0, 30)

  const handleSave = async () => {
    if (!selectedCountry || !serviceName.trim() || !docUrl.trim()) return
    setSaving(true)
    const res = await vendorApiDocsApi.createLink({
      vendor_id: vendor.id,
      country_code: selectedCountry.code,
      country_name: selectedCountry.name,
      service_name: serviceName.trim(),
      doc_url: docUrl.trim(),
      notes: notes.trim() || undefined,
      created_by_name: user?.name,
    })
    if (res.success) { onAdded(); onClose() }
    setSaving(false)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-[var(--bg-primary)] rounded-xl border border-[var(--border-color)] w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
          <h3 className="font-semibold text-[var(--text-primary)]">Add Doc Link — {vendor.name}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          {/* Country picker */}
          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1">Country</label>
            {selectedCountry ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[var(--text-primary)]">{selectedCountry.name} ({selectedCountry.code})</span>
                <button onClick={() => setSelectedCountry(null)} className="text-xs text-primary hover:underline">Change</button>
              </div>
            ) : (
              <>
                <input value={countrySearch} onChange={e => setCountrySearch(e.target.value)} placeholder="Search countries…" autoFocus
                  className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]" />
                <div className="max-h-32 overflow-y-auto mt-1 space-y-0.5">
                  {countryEntries.map(e => (
                    <button key={e.code} onClick={() => { setSelectedCountry(e); setCountrySearch('') }}
                      className="w-full flex items-center justify-between px-3 py-1.5 text-xs rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-primary)]">
                      <span>{e.name}</span>
                      <span className="text-[var(--text-muted)] font-mono">{e.code}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1">Service Name</label>
            <input value={serviceName} onChange={e => setServiceName(e.target.value)} placeholder="e.g. China ID Verification"
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]" />
          </div>

          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1">Documentation URL</label>
            <input value={docUrl} onChange={e => setDocUrl(e.target.value)} placeholder="https://docs.datazoo.com/api/china-id-verification"
              className="w-full px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]" />
          </div>

          <div>
            <label className="text-xs text-[var(--text-muted)] block mb-1">Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Name must be in Chinese characters"
              rows={2} className="w-full px-3 py-2 text-xs rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)] resize-y" />
          </div>

          <button onClick={handleSave} disabled={!selectedCountry || !serviceName.trim() || !docUrl.trim() || saving}
            className="w-full py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-50 transition-colors">
            {saving ? 'Adding…' : 'Add Link'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Source Report Card (expandable) ──────────────────────────────────

function SourceReportCard({ report }: { report: VendorSourceReport }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <div className="border-b border-[var(--border-color)] last:border-b-0">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-[var(--bg-tertiary)] transition-colors text-left">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="text-sm font-medium text-[var(--text-primary)]">{report.source_name}</span>
          {report.source_type && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--bg-tertiary)] text-[var(--text-muted)]">{report.source_type}</span>
          )}
          {report.consent_required === 'YES' && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400">Consent</span>
          )}
          {report.adult_coverage && (
            <span className="text-xs text-[var(--text-muted)]">{report.adult_coverage} coverage</span>
          )}
        </div>
        <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-3 space-y-2">
          {report.description && (
            <p className="text-xs text-[var(--text-secondary)]">{report.description}</p>
          )}
          <div className="grid grid-cols-2 gap-3 text-xs">
            {report.required_fields && (
              <div>
                <p className="font-semibold text-[var(--text-primary)] mb-0.5">Required Fields</p>
                <p className="text-[var(--text-muted)]">{report.required_fields}</p>
              </div>
            )}
            {report.verifiable_elements && (
              <div>
                <p className="font-semibold text-[var(--text-primary)] mb-0.5">Verifiable Elements</p>
                <p className="text-[var(--text-muted)]">{report.verifiable_elements}</p>
              </div>
            )}
          </div>
          <div className="flex gap-4 text-xs text-[var(--text-muted)]">
            {report.refresh_frequency && <span>Refresh: {report.refresh_frequency}</span>}
            {report.credit_footprint && <span>Credit footprint: {report.credit_footprint}</span>}
            {report.source_last_updated && <span>Source updated: {report.source_last_updated}</span>}
          </div>
          {report.external_notes && (
            <div className="p-2.5 rounded bg-[var(--bg-tertiary)] text-xs text-[var(--text-secondary)] whitespace-pre-wrap">
              <p className="font-semibold text-[var(--text-primary)] mb-1">Notes</p>
              {report.external_notes}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Vendor Detail View ───────────────────────────────────────────────

function VendorDetail({ vendor, onBack }: { vendor: VendorApiVendor; onBack: () => void }) {
  const [links, setLinks] = useState<VendorApiLink[]>([])
  const [reports, setReports] = useState<VendorSourceReport[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [search, setSearch] = useState('')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [importing, setImporting] = useState(false)
  const [tab, setTab] = useState<'links' | 'sources'>('links')
  const user = useAuthStore(s => s.user)

  const load = useCallback(async () => {
    setLoading(true)
    const [linksRes, reportsRes] = await Promise.all([
      vendorApiDocsApi.listLinks({ vendor_id: vendor.id }),
      vendorApiDocsApi.listSourceReports({ vendor_id: vendor.id }),
    ])
    if (linksRes.success && linksRes.data) setLinks(linksRes.data)
    if (reportsRes.success && reportsRes.data) setReports(reportsRes.data)
    setLoading(false)
  }, [vendor.id])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this doc link?')) return
    setDeletingId(id)
    await vendorApiDocsApi.deleteLink(id)
    await load()
    setDeletingId(null)
  }

  const handleImportXlsx = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setImporting(true)

    const data = await file.arrayBuffer()
    const wb = XLSX.read(data)
    const ws = wb.Sheets[wb.SheetNames[0]]
    const json = XLSX.utils.sheet_to_json<Record<string, string>>(ws)

    const rows = json.map(row => ({
      country_name: row['Country Name'] || '',
      country_code: COUNTRY_CODES[row['Country Name'] || ''] || undefined,
      source_name: row['Data Source Name'] || '',
      source_type: row['Data Source Type'] || undefined,
      description: row['Data Source Description'] || undefined,
      adult_coverage: row['Adult Population Coverage'] || undefined,
      refresh_frequency: row['Data Refresh Frequency'] || undefined,
      required_fields: row['Required Fields'] || undefined,
      verifiable_elements: row['Verifiable Elements'] || undefined,
      consent_required: row['Consent Required'] || undefined,
      credit_footprint: row['Credit Footprint'] || undefined,
      external_notes: row['External Notes'] || undefined,
      source_last_updated: row['Source Last Updated'] || undefined,
    })).filter(r => r.country_name && r.source_name)

    await vendorApiDocsApi.importSourceReports({
      vendor_id: vendor.id,
      source_file: file.name,
      imported_by_name: user?.name,
      rows,
    })

    setImporting(false)
    setTab('sources')
    await load()
    e.target.value = ''
  }

  // Filter and group links by country
  const filteredLinks = links.filter(l =>
    !search || l.country_name.toLowerCase().includes(search.toLowerCase()) ||
    l.service_name.toLowerCase().includes(search.toLowerCase()) ||
    l.country_code.toLowerCase().includes(search.toLowerCase())
  )
  const groupedLinks: Record<string, VendorApiLink[]> = {}
  for (const link of filteredLinks) {
    const key = `${link.country_code}-${link.country_name}`
    if (!groupedLinks[key]) groupedLinks[key] = []
    groupedLinks[key].push(link)
  }
  const linkCountries = Object.entries(groupedLinks).sort(([, a], [, b]) => a[0].country_name.localeCompare(b[0].country_name))

  // Filter and group source reports by country
  const filteredReports = reports.filter(r =>
    !search || r.country_name.toLowerCase().includes(search.toLowerCase()) ||
    r.source_name.toLowerCase().includes(search.toLowerCase()) ||
    (r.external_notes || '').toLowerCase().includes(search.toLowerCase())
  )
  const groupedReports: Record<string, VendorSourceReport[]> = {}
  for (const r of filteredReports) {
    const key = r.country_code || r.country_name
    if (!groupedReports[key]) groupedReports[key] = []
    groupedReports[key].push(r)
  }
  const reportCountries = Object.entries(groupedReports).sort(([, a], [, b]) => a[0].country_name.localeCompare(b[0].country_name))

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="mt-1 p-1 rounded hover:bg-[var(--bg-tertiary)] text-[var(--text-muted)]">
            <ChevronRight size={18} className="rotate-180" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-[var(--text-primary)]">{vendor.name}</h2>
            {vendor.docs_base_url && (
              <a href={vendor.docs_base_url} target="_blank" rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1 mt-1">
                {vendor.docs_base_url} <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[var(--border-color)] hover:bg-[var(--bg-tertiary)] transition-colors cursor-pointer ${importing ? 'opacity-50 pointer-events-none' : ''}`}>
            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {importing ? 'Importing…' : 'Import XLSX'}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImportXlsx} className="hidden" />
          </label>
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 transition-colors">
            <Plus size={14} /> Add Link
          </button>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-4 border-b border-[var(--border-color)]">
        <button onClick={() => setTab('links')}
          className={`flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors ${tab === 'links' ? 'border-primary text-primary' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}>
          <ExternalLink size={13} /> Doc Links ({links.length})
        </button>
        <button onClick={() => setTab('sources')}
          className={`flex items-center gap-1.5 pb-2 text-sm font-medium border-b-2 transition-colors ${tab === 'sources' ? 'border-primary text-primary' : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text-secondary)]'}`}>
          <FileSpreadsheet size={13} /> Source Report ({reports.length})
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search countries, services, or notes…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] text-[var(--text-primary)]" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading…
        </div>
      ) : tab === 'links' ? (
        /* ── Doc Links Tab ── */
        linkCountries.length === 0 ? (
          <div className="text-center py-12 rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)]">
            <Book size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
            <p className="text-sm text-[var(--text-muted)]">{search ? 'No matching links.' : 'No doc links yet.'}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">Click "Add Link" to add API documentation references.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {linkCountries.map(([key, countryLinks]) => (
              <div key={key} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
                <div className="px-4 py-2.5 bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                    {countryLinks[0].country_name}
                    <span className="text-xs font-normal text-[var(--text-muted)] ml-2">{countryLinks[0].country_code}</span>
                    <span className="text-xs font-normal text-[var(--text-muted)] ml-2">· {countryLinks.length} service{countryLinks.length !== 1 ? 's' : ''}</span>
                  </h3>
                </div>
                <div className="divide-y divide-[var(--border-color)]">
                  {countryLinks.map(link => (
                    <div key={link.id} className="px-4 py-2.5 flex items-center justify-between hover:bg-[var(--bg-tertiary)] transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <a href={link.doc_url} target="_blank" rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline flex items-center gap-1.5 font-medium">
                            {link.service_name} <ExternalLink size={12} />
                          </a>
                        </div>
                        {link.notes && (
                          <p className="text-xs text-[var(--text-muted)] mt-0.5 flex items-center gap-1">
                            <StickyNote size={10} /> {link.notes}
                          </p>
                        )}
                      </div>
                      <button onClick={() => handleDelete(link.id)} disabled={deletingId === link.id}
                        className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-600 transition-colors flex-shrink-0">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* ── Source Report Tab ── */
        reportCountries.length === 0 ? (
          <div className="text-center py-12 rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)]">
            <FileSpreadsheet size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
            <p className="text-sm text-[var(--text-muted)]">{search ? 'No matching sources.' : 'No source report imported yet.'}</p>
            <p className="text-xs text-[var(--text-muted)] mt-1">Use "Import XLSX" to upload a vendor source report.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {reportCountries.map(([key, countryReports]) => (
              <div key={key} className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] overflow-hidden">
                <div className="px-4 py-2.5 bg-[var(--bg-tertiary)] border-b border-[var(--border-color)]">
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                    {countryReports[0].country_name}
                    {countryReports[0].country_code && (
                      <span className="text-xs font-normal text-[var(--text-muted)] ml-2">{countryReports[0].country_code}</span>
                    )}
                    <span className="text-xs font-normal text-[var(--text-muted)] ml-2">· {countryReports.length} source{countryReports.length !== 1 ? 's' : ''}</span>
                  </h3>
                </div>
                {countryReports.map(report => (
                  <SourceReportCard key={report.id} report={report} />
                ))}
              </div>
            ))}
          </div>
        )
      )}

      {showAdd && <AddLinkModal vendor={vendor} onClose={() => setShowAdd(false)} onAdded={load} />}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────

export function VendorApiDocs() {
  const [vendors, setVendors] = useState<VendorApiVendor[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedVendor, setSelectedVendor] = useState<VendorApiVendor | null>(null)
  const [showAddVendor, setShowAddVendor] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await vendorApiDocsApi.listVendors()
    if (res.success && res.data) setVendors(res.data)
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const handleDeleteVendor = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!confirm('Delete this vendor and all its doc links?')) return
    setDeletingId(id)
    await vendorApiDocsApi.deleteVendor(id)
    await load()
    setDeletingId(null)
  }

  if (selectedVendor) {
    return <VendorDetail vendor={selectedVendor} onBack={() => { setSelectedVendor(null); load() }} />
  }

  const totalLinks = vendors.reduce((sum, v) => sum + (v.link_count || 0), 0)

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)] flex items-center gap-3">
            <Book size={20} />
            Vendor Docs
          </h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Quick-reference links to vendor documentation, source reports, and coverage data by country.
          </p>
        </div>
        <button onClick={() => setShowAddVendor(true)}
          className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-teal-600 text-white hover:bg-teal-500 transition-colors">
          <Plus size={14} /> Add Vendor
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-[var(--text-primary)]">{vendors.length}</p>
          <p className="text-xs text-[var(--text-muted)]">Vendors</p>
        </div>
        <div className="p-4 text-center rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <p className="text-2xl font-bold text-primary">{totalLinks}</p>
          <p className="text-xs text-[var(--text-muted)]">Doc Links</p>
        </div>
      </div>

      {/* Vendor List */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading…
        </div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-12 rounded-lg border border-dashed border-[var(--border-color)] bg-[var(--bg-secondary)]">
          <Building2 size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
          <p className="text-sm text-[var(--text-muted)]">No vendors yet.</p>
          <p className="text-xs text-[var(--text-muted)] mt-1">Click "Add Vendor" to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {vendors.map(v => (
            <div key={v.id}
              className="p-4 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] hover:border-primary/30 transition-colors cursor-pointer"
              onClick={() => setSelectedVendor(v)}>
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <Building2 size={16} className="text-primary flex-shrink-0" />
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">{v.name}</h3>
                    <span className="text-xs text-[var(--text-muted)]">{v.link_count || 0} link{(v.link_count || 0) !== 1 ? 's' : ''}</span>
                    {(v.source_count || 0) > 0 && (
                      <span className="text-xs text-[var(--text-muted)]">{v.source_count} source{v.source_count !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 ml-7 text-xs text-[var(--text-muted)]">
                    {v.docs_base_url && (
                      <span className="truncate max-w-xs">{v.docs_base_url}</span>
                    )}
                    {(() => {
                      const lastActivity = v.latest_link_at && v.latest_import_at
                        ? (v.latest_link_at > v.latest_import_at ? v.latest_link_at : v.latest_import_at)
                        : v.latest_link_at || v.latest_import_at
                      const formatted = formatDate(lastActivity)
                      return formatted ? (
                        <span className="flex items-center gap-1 flex-shrink-0">
                          <Clock size={11} /> Updated {formatted}
                        </span>
                      ) : null
                    })()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={(e) => handleDeleteVendor(v.id, e)} disabled={deletingId === v.id}
                    className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-500/10 text-[var(--text-muted)] hover:text-red-600 transition-colors">
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={18} className="text-[var(--text-muted)] flex-shrink-0" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddVendor && <AddVendorModal onClose={() => setShowAddVendor(false)} onAdded={load} />}
    </div>
  )
}
